/**
 * Security utilities for the Komentas widget
 * Provides content sanitization, XSS prevention, and secure API communication
 */

/**
 * HTML sanitizer to prevent XSS attacks
 */
export class HtmlSanitizer {
  // Allowed HTML tags for comment content
  private static readonly ALLOWED_TAGS = new Set([
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a', 'blockquote', 'code', 'pre'
  ]);

  // Allowed attributes for specific tags
  private static readonly ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
    'a': new Set(['href', 'title', 'target', 'rel']),
    'blockquote': new Set(['cite']),
    'code': new Set(['class']),
    'pre': new Set(['class'])
  };

  // Safe URL schemes
  private static readonly ALLOWED_SCHEMES = new Set([
    'http:', 'https:', 'mailto:', 'tel:'
  ]);

  /**
   * Sanitizes HTML content to prevent XSS attacks
   */
  static sanitize(html: string): string {
    if (!html) return '';
    
    // Handle whitespace-only strings
    if (html.trim() === '' && html.length > 0) {
      return html; // Preserve whitespace
    }

    // Remove script tags and their content before parsing
    let sanitized = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<script\b[^>]*>/gi, '');

    // Create a temporary DOM element to parse HTML
    const doc = new DOMParser().parseFromString(sanitized, 'text/html');
    const walker = document.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      null
    );

    const elementsToProcess: { node: Node; parent: Node }[] = [];

    // Collect all nodes for processing
    let currentNode: Node | null;
    while (currentNode = walker.nextNode()) {
      elementsToProcess.push({
        node: currentNode,
        parent: currentNode.parentNode!
      });
    }

    // Process nodes in reverse order to avoid issues with tree modifications
    for (let i = elementsToProcess.length - 1; i >= 0; i--) {
      const { node } = elementsToProcess[i];
      
      if (node.nodeType === Node.TEXT_NODE) {
        // Text nodes are safe, keep as-is
        continue;
      }
      
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();
        
        if (!this.ALLOWED_TAGS.has(tagName)) {
          // Remove disallowed tags but keep their text content
          this.replaceWithTextContent(element);
          continue;
        }
        
        // Sanitize attributes
        this.sanitizeAttributes(element);
      }
    }

    return doc.body.innerHTML;
  }

  /**
   * Replace element with its text content
   */
  private static replaceWithTextContent(element: Element): void {
    const textNode = document.createTextNode(element.textContent || '');
    element.parentNode?.replaceChild(textNode, element);
  }

  /**
   * Sanitize element attributes
   */
  private static sanitizeAttributes(element: Element): void {
    const tagName = element.tagName.toLowerCase();
    const allowedAttrs = this.ALLOWED_ATTRIBUTES[tagName] || new Set();
    
    // Get all attributes to avoid modifying while iterating
    const attributes = Array.from(element.attributes);
    
    for (const attr of attributes) {
      const attrName = attr.name.toLowerCase();
      
      if (!allowedAttrs.has(attrName)) {
        element.removeAttribute(attr.name);
        continue;
      }
      
      // Special handling for URLs
      if (attrName === 'href' || attrName === 'src') {
        const url = this.sanitizeUrl(attr.value);
        if (url) {
          element.setAttribute(attr.name, url);
        } else {
          element.removeAttribute(attr.name);
        }
      }
      
      // Ensure external links have proper security attributes
      if (attrName === 'href' && element.tagName.toLowerCase() === 'a') {
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener noreferrer');
      }
    }
  }

  /**
   * Sanitize URLs to prevent javascript: and data: schemes
   */
  private static sanitizeUrl(url: string): string | null {
    if (!url) return null;
    
    try {
      const parsed = new URL(url, window.location.origin);
      
      if (this.ALLOWED_SCHEMES.has(parsed.protocol)) {
        return parsed.toString();
      }
      
      return null;
    } catch {
      // If URL parsing fails, treat as relative URL
      if (url.startsWith('/') && !url.startsWith('//')) {
        return url;
      }
      return null;
    }
  }

  /**
   * Escape HTML entities in plain text
   */
  static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

/**
 * CSRF protection utilities
 */
export class CsrfProtection {
  private static token: string | null = null;
  private static readonly TOKEN_HEADER = 'X-CSRF-Token';
  private static readonly TOKEN_META_NAME = 'csrf-token';

  /**
   * Initialize CSRF protection by getting token from meta tag or generating one
   */
  static initialize(): void {
    // Try to get CSRF token from meta tag first
    const metaTag = document.querySelector(`meta[name="${this.TOKEN_META_NAME}"]`) as HTMLMetaElement;
    
    if (metaTag && metaTag.content) {
      this.token = metaTag.content;
    } else {
      // Generate a random token if none exists
      this.token = this.generateToken();
    }
  }

  /**
   * Get the current CSRF token
   */
  static getToken(): string | null {
    return this.token;
  }

  /**
   * Add CSRF token to request headers
   */
  static addTokenToHeaders(headers: Record<string, string>): Record<string, string> {
    if (this.token) {
      headers[this.TOKEN_HEADER] = this.token;
    }
    return headers;
  }

  /**
   * Generate a random CSRF token
   */
  private static generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }
}

/**
 * Input validation utilities
 */
export class InputValidator {
  /**
   * Validate comment content
   */
  static validateComment(content: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!content || content.trim().length === 0) {
      errors.push('Comment content cannot be empty');
    }
    
    if (content.length > 10000) {
      errors.push('Comment content is too long (maximum 10,000 characters)');
    }
    
    // Check for potential spam patterns
    if (this.containsSpamPatterns(content)) {
      errors.push('Comment contains potential spam content');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate email address
   */
  static validateEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  /**
   * Validate username
   */
  static validateUsername(username: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!username || username.trim().length === 0) {
      errors.push('Username cannot be empty');
    }
    
    if (username.length < 2 || username.length > 50) {
      errors.push('Username must be between 2 and 50 characters');
    }
    
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      errors.push('Username can only contain letters, numbers, underscores, and hyphens');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Check for common spam patterns
   */
  private static containsSpamPatterns(content: string): boolean {
    const spamPatterns = [
      /(.)\1{10,}/i, // Repeated characters
      /(https?:\/\/[^\s]+){3,}/i, // Multiple URLs
      /\b(buy|sell|cheap|free|click|download|viagra|casino|lottery|winner)\b.*\b(now|today|here|link)\b/i,
      /\b[A-Z]{5,}\b.*\b[A-Z]{5,}\b/, // Multiple uppercase words (case-sensitive)
    ];
    
    return spamPatterns.some(pattern => pattern.test(content));
  }
}

/**
 * Rate limiting for client-side operations
 */
export class RateLimiter {
  private static operations = new Map<string, { count: number; resetTime: number }>();
  
  /**
   * Check if operation is allowed within rate limit
   */
  static isAllowed(operationType: string, maxRequests: number, windowMs: number): boolean {
    const now = Date.now();
    const key = operationType;
    const operation = this.operations.get(key);
    
    if (!operation || now >= operation.resetTime) {
      // Reset or initialize counter
      this.operations.set(key, { count: 1, resetTime: now + windowMs });
      return true;
    }
    
    if (operation.count >= maxRequests) {
      return false;
    }
    
    operation.count++;
    return true;
  }

  /**
   * Get time until rate limit resets
   */
  static getTimeUntilReset(operationType: string): number {
    const operation = this.operations.get(operationType);
    if (!operation) return 0;
    
    return Math.max(0, operation.resetTime - Date.now());
  }
}

/**
 * Secure storage utilities
 */
export class SecureStorage {
  private static readonly PREFIX = 'komentas_widget_';
  
  /**
   * Store sensitive data with basic encryption (for non-critical data)
   */
  static setItem(key: string, value: string): void {
    try {
      const encrypted = btoa(value); // Basic encoding (not encryption)
      localStorage.setItem(this.PREFIX + key, encrypted);
    } catch (error) {
      console.warn('Failed to store data:', error);
    }
  }

  /**
   * Retrieve and decrypt sensitive data
   */
  static getItem(key: string): string | null {
    try {
      const encrypted = localStorage.getItem(this.PREFIX + key);
      if (!encrypted) return null;
      
      return atob(encrypted);
    } catch (error) {
      console.warn('Failed to retrieve data:', error);
      return null;
    }
  }

  /**
   * Remove stored data
   */
  static removeItem(key: string): void {
    localStorage.removeItem(this.PREFIX + key);
  }

  /**
   * Clear all widget-related data
   */
  static clear(): void {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.PREFIX)) {
        keys.push(key);
      }
    }
    
    keys.forEach(key => localStorage.removeItem(key));
  }
}

/**
 * Content Security Policy utilities
 */
export class CspUtils {
  /**
   * Check if current page allows inline scripts (for security warning)
   */
  static hasStrictCsp(): boolean {
    const metaTags = document.querySelectorAll('meta[http-equiv="Content-Security-Policy"]');
    
    for (const metaTag of metaTags) {
      const content = (metaTag as HTMLMetaElement).content;
      if (content && content.includes("script-src") && !content.includes("'unsafe-inline'")) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Warn about potential CSP issues
   */
  static warnIfCspIssues(): void {
    if (this.hasStrictCsp()) {
      console.warn('Komentas Widget: Strict CSP detected. Ensure widget scripts are properly whitelisted.');
    }
  }
}